import React, { useCallback, useEffect, useMemo, useState, } from 'react';
import clsx from 'clsx';
import { Route, matchPath, withRouter, } from 'react-router-dom';
import Box from '@mui/material/Box';
import api, { ignoreRequestFailure } from '../services/api';
import Footer from '../components/Footer';
import MainHeader from '../components/Header';
import { useNurseryContext } from '../contexts/NurseryContext';
import { useSearchContext } from '../contexts/SearchContext';
import SearchBar from '../components/Search/SearchBar';
import { cleanSearch, isPDPPage, resetParam } from '../utils/helpers';
import DetailLayout from '../Routes/DetailLayout';
import { ShopifyCart } from '../components/Shopify';
import shopifyAPI from '../services/shopifyAPI';
import { fetchCart } from '../utils/graphQLMutations';
import ShopifyCartIcon from '../themes/icons/ShopifyCartIcon';
import CustomButton from '../components/CommonComponents/CustomButton';
import { mainLayoutStyles } from '../Routes/LayoutStyles';
const normalizeNavigation = (navigationInput) => {
    var _a;
    if (!navigationInput) {
        return null;
    }
    if (Array.isArray(navigationInput)) {
        return navigationInput;
    }
    return (_a = navigationInput.navigation) !== null && _a !== void 0 ? _a : null;
};
const SharedApp = ({ data, history, location, }) => {
    const { navigation: navigationPayload, commonData, apiData = null } = data;
    const normalizedInitialNavigation = useMemo(() => {
        const normalized = normalizeNavigation(navigationPayload);
        return normalized || [];
    }, [navigationPayload]);
    const { setNurseryName, setNurserySlug, isNursery, nurseryName, nurserySlug, } = useNurseryContext();
    const { searchTerm, activeZone, origSearchTerm, spellingSuggestion, setSearchTerm, setOrigSearchTerm, setActiveZone, } = useSearchContext();
    const [mobNavOpen, setMobNavOpen] = useState(false);
    const [checkout, setCheckout] = useState();
    const [cartOpen, setCartOpen] = useState(false);
    const [cartCount, setCartCount] = useState(0);
    const detailNavigation = useMemo(() => {
        if (!apiData || !apiData.nursery_navigation) {
            return null;
        }
        return normalizeNavigation(apiData.nursery_navigation);
    }, [apiData]);
    const routeStartsOnNurseryOrDetail = useMemo(() => {
        if (location.pathname.startsWith('/nurseries')) {
            return true;
        }
        const plantDetailMatch = matchPath(location.pathname, {
            path: ['/plants/:plantId(\\d+)/:plantName/', '/plants/:plantId(\\d+)/:plantName'],
            exact: true,
        });
        return Boolean(plantDetailMatch);
    }, [location.pathname]);
    const initialNavigationData = useMemo(() => {
        if (routeStartsOnNurseryOrDetail && detailNavigation && detailNavigation.length > 0) {
            return detailNavigation;
        }
        return normalizedInitialNavigation;
    }, [routeStartsOnNurseryOrDetail, detailNavigation, normalizedInitialNavigation]);
    const [navigationData, setNavigationData] = useState(initialNavigationData);
    const [defaultNavigationData, setDefaultNavigationData] = useState(normalizedInitialNavigation);
    const [navigationOverrideActive, setNavigationOverrideActive] = useState(routeStartsOnNurseryOrDetail && !!detailNavigation && detailNavigation.length > 0);
    const notCartPage = !location.pathname.startsWith('/cart');
    const routeRequiresNurseryNavigation = useCallback(() => {
        if (location.pathname.startsWith('/nurseries')) {
            return true;
        }
        const plantDetailMatch = matchPath(location.pathname, {
            path: ['/plants/:plantId(\\d+)/:plantName/', '/plants/:plantId(\\d+)/:plantName'],
            exact: true,
        });
        return Boolean(plantDetailMatch);
    }, [location.pathname]);
    const restoreMainNavigation = useCallback(() => {
        if (defaultNavigationData && defaultNavigationData.length > 0) {
            setNavigationData(defaultNavigationData);
        }
        setNavigationOverrideActive(false);
    }, [defaultNavigationData]);
    const applyNavigationOverride = useCallback((nav) => {
        setNavigationData(nav);
        setNavigationOverrideActive(true);
    }, []);
    const handleSetNavigationData = useCallback((navData) => {
        const normalizedNavigation = normalizeNavigation(navData);
        if (!normalizedNavigation || normalizedNavigation.length === 0) {
            restoreMainNavigation();
            return;
        }
        applyNavigationOverride(normalizedNavigation);
    }, [applyNavigationOverride, restoreMainNavigation]);
    useEffect(() => {
        if (navigationOverrideActive && !routeRequiresNurseryNavigation()) {
            restoreMainNavigation();
        }
    }, [location.pathname, location.search, navigationOverrideActive, restoreMainNavigation, routeRequiresNurseryNavigation]);
    useEffect(() => {
        if (normalizedInitialNavigation.length === 0) {
            return;
        }
        setDefaultNavigationData(normalizedInitialNavigation);
        if (!navigationOverrideActive) {
            setNavigationData(normalizedInitialNavigation);
        }
    }, [normalizedInitialNavigation, navigationOverrideActive]);
    useEffect(() => {
        if (!routeRequiresNurseryNavigation()) {
            return;
        }
        if (detailNavigation && detailNavigation.length > 0) {
            applyNavigationOverride(detailNavigation);
        }
    }, [routeRequiresNurseryNavigation, detailNavigation, applyNavigationOverride]);
    const changeQuantity = async (plant, modifyNursery, includeShipping, zipCode, province) => {
        const newPlant = {
            set_quantity: plant.quantity,
            plant_id: plant.plant_id,
            variant_id: plant.variant_id,
        };
        const newNurseries = checkout.nurseries.map((nursery) => {
            const item = nursery;
            if (item.name === modifyNursery.name) {
                item.shippingLoading = true;
            }
            return item;
        });
        setCheckout({ ...checkout, nurseries: newNurseries });
        await api.checkout.addItems(newPlant).then(async (cart) => {
            let qty = 0;
            cart.cart.nurseries.forEach((nursery) => {
                nursery.items.forEach((item) => {
                    qty += item.quantity;
                });
            });
            if (!includeShipping) {
                const newCheckCart = cart.cart;
                newCheckCart.nurseries = cart.cart.nurseries
                    .map((nursery) => {
                    const newNursery = nursery;
                    newNursery.checked = true;
                    newNursery.shippingLoading = false;
                    return newNursery;
                });
                setCheckout(newCheckCart);
                setCartCount(qty);
            }
            else {
                await api.checkout.getCartList(`?include_shipping=true&zip=${zipCode}&province=${province}`).then((cartList) => {
                    const shippedCheckCart = cartList.cart;
                    shippedCheckCart.nurseries = cartList.cart.nurseries
                        .map((nursery) => {
                        const newNursery = nursery;
                        newNursery.checked = true;
                        newNursery.shippingLoading = false;
                        return newNursery;
                    });
                    setCheckout(shippedCheckCart);
                    setCartCount(qty);
                })
                    .catch(ignoreRequestFailure);
            }
        })
            .catch(ignoreRequestFailure);
    };
    const addToShopifyCart = async (variant, increase_quantity, djangoPk) => {
        const lineItems = {
            plant_id: djangoPk,
            increase_quantity,
            variant_id: variant.id,
        };
        await api.checkout.addItems(lineItems).then((cart) => {
            let qty = 0;
            cart.cart.nurseries.forEach((nursery) => {
                nursery.items.forEach((item) => {
                    qty += item.quantity;
                });
            });
            const newCheckCart = cart.cart;
            newCheckCart.nurseries = cart.cart.nurseries.map((nursery) => {
                const newNursery = nursery;
                newNursery.checked = true;
                return newNursery;
            });
            setCartOpen(true);
            setCheckout(newCheckCart);
            setCartCount(qty);
        })
            .catch(ignoreRequestFailure);
    };
    const changeWrapperClass = () => {
        setMobNavOpen(!mobNavOpen);
    };
    const zoneSetHandler = (zone) => {
        setActiveZone(zone);
        // On the front page, just hold the zone in context —
        // it gets included in the query when a search is performed (via setQueries)
        if (location.pathname === '/') {
            return;
        }
        let searchString = '';
        const search = cleanSearch(location.search);
        const path = location.pathname;
        if (zone === '0') {
            searchString = resetParam(search, 'zone', false);
        }
        else {
            searchString = resetParam(search, 'zone', zone);
        }
        if (origSearchTerm !== ''
            && spellingSuggestion.spelling_suggestion != null) {
            searchString = searchString.replace(origSearchTerm, spellingSuggestion.spelling_suggestion);
            setOrigSearchTerm('');
        }
        if (typeof window !== 'undefined') {
            if (window.location.href.indexOf('/search/') !== -1
                || window.location.href.indexOf('/featured/') !== -1
                || window.location.href.indexOf('nurseries') !== -1
                || window.location.href.indexOf('/plants/genus/') !== -1
                || window.location.href.indexOf('/plants/tags/') !== -1
                || !isPDPPage()) {
                if (path.length - 1 === path.lastIndexOf('/')) {
                    history.push(`${path}?${searchString}`);
                }
                else {
                    history.push(`${path}/?${searchString}`);
                }
                gtag('event', 'page_view');
            }
            else {
                window.location.href = `/search/?${searchString}`;
            }
        }
    };
    const setQueries = (query, rawSearch = '', queryType = '', slug = '', url = '') => {
        let newQuery = query;
        let nextNurseryName = nurseryName;
        let nextNurserySlug = nurserySlug;
        let nextIsNursery = isNursery;
        if (queryType === 'Nursery') {
            setNurseryName(rawSearch);
            nextNurseryName = rawSearch;
            if (slug !== null) {
                setNurserySlug(slug);
                nextNurserySlug = slug;
            }
            nextIsNursery = true;
        }
        gtag('event', 'search', { search_term: rawSearch });
        setSearchTerm(rawSearch);
        const clearedZone = newQuery.indexOf('&zone=null') !== -1;
        if (clearedZone) {
            newQuery = newQuery.replace('&zone=null', '');
            setActiveZone('0');
        }
        const nextActiveZone = clearedZone ? '0' : activeZone;
        if (nextActiveZone !== '0' && newQuery.indexOf('zone') === -1) {
            newQuery = `${newQuery}&zone=${nextActiveZone}`;
        }
        if (typeof window !== 'undefined') {
            let originType = 'search';
            if (window.location.pathname.indexOf('/plants/') !== -1) {
                originType = 'plant_detail';
            }
            if (window.location.pathname.indexOf('/nurseries/') !== -1) {
                originType = 'nursery';
            }
            if (window.location.pathname === '/') {
                originType = 'home';
            }
            if (nextIsNursery) {
                switch (originType) {
                    case 'plant_detail':
                        if (!nextNurserySlug && nextNurseryName) {
                            const derivedSlug = nextNurseryName
                                .toLowerCase()
                                .split(' ')
                                .join('-');
                            setNurserySlug(derivedSlug);
                            nextNurserySlug = derivedSlug;
                        }
                        if (newQuery.indexOf('nurseries=') === -1) {
                            window.location.href = `/nurseries/${nextNurserySlug}/?${newQuery}`;
                        }
                        else
                            window.location.href = `/nurseries/${nextNurserySlug}`;
                        break;
                    case 'nursery':
                        if (!slug) {
                            history.push(`${window.location.pathname}?${newQuery}`);
                        }
                        else {
                            history.push(`/nurseries/${nextNurserySlug}`);
                        }
                        gtag('event', 'page_view');
                        break;
                    case 'home':
                    case 'search':
                    default:
                        window.location.href = `/nurseries/${nextNurserySlug}/`;
                        break;
                }
            }
            else if (url !== '') {
                window.location.href = url;
            }
            else if (window.location.href.indexOf('/search/') !== -1) {
                const newSearch = window.location.search.replace('?', '');
                if (newSearch === newQuery) {
                    return;
                }
                history.push(`/search/?${newQuery}`, { getAPIData: true });
                gtag('event', 'page_view');
            }
            else {
                window.location.href = `/search/?${newQuery}`;
            }
        }
    };
    const setCartData = async (cart) => {
        let qty = 0;
        cart.cart.nurseries.forEach((nursery) => {
            nursery.items.forEach((item) => {
                qty += item.quantity;
            });
        });
        const newCheckCart = cart.cart;
        newCheckCart.nurseries = cart.cart.nurseries
            .map((nursery) => {
            const newNursery = nursery;
            newNursery.checked = true;
            newNursery.shippingLoading = true;
            return newNursery;
        });
        setCheckout(newCheckCart);
        setCartCount(qty);
    };
    const handleCart = async () => {
        const cartId = localStorage.getItem('shopifyCartId');
        if (cartId) {
            await shopifyAPI.request(fetchCart, { variables: { shopifyCartId: cartId } }).then(async ({ data: cartData }) => {
                // If we have a cart id but it no longer exists then it means that the user has made the purchase
                // so we clear our local cart cookies
                if (cartData.node === null) {
                    api.checkout.clearCart().then(() => {
                        localStorage.removeItem('shopifyCartId');
                        api.checkout.getCartList()
                            .then((resp) => {
                            setCartData(resp);
                        })
                            .catch(ignoreRequestFailure);
                    });
                }
            });
        }
        else {
            api.checkout.getCartList()
                .then((resp) => {
                setCartData(resp);
            })
                .catch(ignoreRequestFailure);
        }
    };
    const closeCart = () => {
        setCartOpen(false);
    };
    useEffect(() => {
        if (typeof window !== 'undefined') {
            const urlParams = new URLSearchParams(window.location.search);
            const urlZone = urlParams.get('zone');
            if (urlZone && urlZone !== 'null' && urlZone !== '') {
                setActiveZone(urlZone);
            }
        }
        // Handle Cart functionality
        (handleCart)();
    }, []);
    return (React.createElement("div", { className: clsx('wrap', mobNavOpen && 'mobNavOpen') },
        React.createElement(MainHeader, { activePromo: commonData.promo_link, openCartOnClick: () => setCartOpen(true), navigation: navigationData, changeWrapperClass: changeWrapperClass, showNurseryHeader: false, cartCount: cartCount }),
        notCartPage && (React.createElement(Box, { sx: [mainLayoutStyles.fullWidthContent, { marginTop: '58px' }] },
            React.createElement("div", { className: "fullwidth_search" },
                React.createElement("div", { className: "search_bar" },
                    React.createElement(Box, { sx: mainLayoutStyles.searchBarContainer },
                        React.createElement(SearchBar, { history: history, searchTerm: searchTerm, setQueries: setQueries, zones: commonData.zones, zoneSetHandler: zoneSetHandler, activeZone: activeZone, showNurseryHeader: () => false, nursery: nurseryName })))))),
        React.createElement(Route, { path: "/plants/:plantId(\\d+)/:plantName/", render: (props) => (React.createElement(DetailLayout, { addToShopifyCart: addToShopifyCart, plantApiData: apiData, setNavigationData: handleSetNavigationData, ...props })) }),
        React.createElement(Footer, null),
        checkout && (React.createElement(React.Fragment, null,
            React.createElement(ShopifyCart, { checkout: checkout, changeQuantity: changeQuantity, count: cartCount, openCart: cartOpen, closeCart: closeCart }),
            React.createElement(CustomButton, { onClick: () => setCartOpen(true), buttonText: cartCount, buttonClassSX: mainLayoutStyles.toggleButton, endIcon: React.createElement(ShopifyCartIcon, null) })))));
};
export default withRouter(SharedApp);
